// Client helpers for inviting teammates: parse a pasted list, talk to the bulk
// "team link" endpoint, and load the remembered contacts. The parsing mirrors
// the server's rules (server/src/team-invites.js) so the UI can show exactly
// who will be invited before anything is sent — the server re-validates.

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

// Most people one bulk invite may carry (server: MAX_BULK_INVITES).
export const MAX_BULK_INVITES = 50;

const EMAIL_RE = /^[^@\s<>,;]+@[^@\s<>,;]+\.[^@\s<>,;]+$/;

export function isValidEmail(email) {
  return typeof email === 'string' && email.length <= 254 && EMAIL_RE.test(email);
}

/** "jane.doe@x.com" → "Jane Doe" — a readable label for a bare address. */
export function nameFromEmail(email) {
  const local = String(email || '').split('@')[0] || '';
  const words = local
    .split(/[._\-+]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return words.join(' ') || local || 'Teammate';
}

/** One entry → `{ email, name }`: a bare address or `Jane Doe <jane@x.com>`. */
export function parseInvitee(entry) {
  const raw = String(entry || '').trim();
  if (!raw) return null;
  const angled = raw.match(/^"?([^"<]*?)"?\s*<([^>]+)>$/);
  const email = (angled ? angled[2] : raw).trim().toLowerCase();
  if (!isValidEmail(email)) return null;
  const name = (angled ? angled[1] : '').trim() || nameFromEmail(email);
  return { email, name };
}

/**
 * Split a pasted list (commas, semicolons, newlines) into
 * `{ invitees, invalid }`, deduped by address.
 */
export function parseInviteeText(text) {
  const invitees = [];
  const invalid = [];
  const seen = new Set();
  for (const part of String(text || '').split(/[\n,;]+/)) {
    if (!part.trim()) continue;
    const parsed = parseInvitee(part);
    if (!parsed) {
      invalid.push(part.trim());
      continue;
    }
    if (seen.has(parsed.email)) continue;
    seen.add(parsed.email);
    invitees.push(parsed);
  }
  return { invitees, invalid };
}

/** The shareable URL for an invite or team-link code. */
export function inviteLink(code) {
  return `${window.location.origin}/?invite=${code}`;
}

/** Copy to the clipboard, falling back to a hidden textarea on older browsers. */
export function copyText(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

function authHeaders() {
  const token = localStorage.getItem('symphonate-token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

/**
 * Invite a list of teammates on one team link. Resolves to the server's
 * `{ batchCode, invites, skipped, emailed }`; rejects with an Error carrying
 * the server's `code` (e.g. `email_unverified`) when it refuses.
 */
export async function sendBulkInvites(workspaceId, invitees, role = 'member') {
  const res = await fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/invites/bulk`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ invitees, role }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || 'Could not send invites');
    err.code = data.code;
    throw err;
  }
  return data;
}

/** People you've invited, who invited you, or share a workspace with. */
export async function fetchInviteContacts() {
  try {
    const res = await fetch(`${SERVER_URL}/api/me/contacts`, { headers: authHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.contacts) ? data.contacts : [];
  } catch {
    return [];
  }
}
